'use client';

import FilmmakerForm from '@/components/forms/FilmmakerForm';
import IndustryForm from '@/components/forms/IndustryForm';
import PersonalInfoForm from '@/components/forms/PersonalInfoForm';
import PressForm from '@/components/forms/PressForm';
import TravelForm from '@/components/forms/TravelForm';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/tailwind-merge';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

const FORM_TYPES = {
    FILMMAKER: 'filmmaker',
    INDUSTRY: 'industry',
    PRESS: 'press',
} as const;

type FormType = (typeof FORM_TYPES)[keyof typeof FORM_TYPES];

const AccreditationTypeButton = ({
    title,
    onClick,
    isActive,
}: {
    isActive: boolean;
    title: string;
    onClick: () => void;
}) => {
    return (
        <Button
            variant="secondary"
            onClick={onClick}
            title={title}
            className={cn(
                `w-full h-20`,
                !isActive &&
                    'bg-transparent border border-solid border-primary text-primary [&>*]:text-primary',
            )}
            textClasses="!text-[32px]"
        />
    );
};

export default function AccreditationForm() {
    const t = useTranslations('AccreditationPage');
    const generalT = useTranslations();
    const [formState, setFormState] = useState<FormType>(FORM_TYPES.FILMMAKER);
    const [isTravelFormActive, setIsTravelFormActive] = useState(false);

    return (
        <div className="flex flex-col sm:flex-row gap-6 w-full mt-5 lg:mt-12">
            <div className="flex flex-col gap-6 w-full">
                <div className="mb-12">
                    <h4 className="mb-5 text-white text-[32px] lg:text-[46px] uppercase">
                        {generalT('Account.generalInformation')}
                    </h4>
                    <PersonalInfoForm />
                </div>
                <div className="flex flex-col w-full h-full h-min-screen gap-10">
                    <div className="flex flex-col gap-7">
                        <p className="font-helvetica-neue my-12 text-[22px] font-light">
                            {t('provideTheFollowing')}
                        </p>
                        <p className="uppercase text-3xl md:text-5xl">{t('selectForm')}</p>
                        <div className="flex max-md:flex-col gap-6 w-full [&>*]:w-full">
                            <AccreditationTypeButton
                                onClick={() => setFormState(FORM_TYPES.FILMMAKER)}
                                title={t('filmmaker')}
                                isActive={formState === FORM_TYPES.FILMMAKER}
                            />
                            <AccreditationTypeButton
                                onClick={() => setFormState(FORM_TYPES.INDUSTRY)}
                                title={t('industry')}
                                isActive={formState === FORM_TYPES.INDUSTRY}
                            />
                            <AccreditationTypeButton
                                onClick={() => setFormState(FORM_TYPES.PRESS)}
                                title={t('press')}
                                isActive={formState === FORM_TYPES.PRESS}
                            />
                        </div>
                    </div>
                    {formState === FORM_TYPES.FILMMAKER && <FilmmakerForm />}
                    {formState === FORM_TYPES.INDUSTRY && <IndustryForm />}
                    {formState === FORM_TYPES.PRESS && <PressForm />}
                </div>

                <div className="w-full">
                    <h4 className="my-5 text-white text-[32px] lg:text-[46px] uppercase">
                        {t('willYouTravel')}
                    </h4>
                    <div className="mb-12 flex items-center gap-5 uppercase text-3xl">
                        <span className="text-white">{generalT('Common.no')}</span>
                        <label className="relative flex items-center cursor-pointer">
                            {/* Background for the toggle */}
                            <input
                                type="checkbox"
                                id="travel-toggle"
                                className="sr-only peer"
                                checked={isTravelFormActive}
                                onChange={() => setIsTravelFormActive(!isTravelFormActive)}
                                aria-label={t('willYouTravel')}
                            />
                            <div className="w-12 h-6 bg-transparent border border-white/70 peer-checked:bg-accent peer-checked:border-transparent rounded-full"></div>
                            {/* The toggle knob */}
                            <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-primary peer-checked:bg-white  rounded-full shadow-md peer-checked:translate-x-6 transition-transform duration-300"></div>
                        </label>
                        <span className="text-white">{generalT('Common.yes')}</span>
                    </div>

                    {isTravelFormActive && (
                        <div className="flex flex-col">
                            <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                                {t('generalGuestInformation')}
                            </h4>
                            <div className="max-w-3xl mb-10 flex flex-col gap-2 text-white font-helvetica-neue text-xl font-light">
                                <p>{t('generalGuestInformationDescription')}</p>
                                <span className="text-accent font-normal">{t('attention')}</span>
                                <p>{t('environmentalEncouragement')}</p>
                            </div>
                            <h4 className="mb-5 text-white text-[32px] lg:text-[46px] uppercase">
                                {t('yourTravelInformation')}
                            </h4>
                            <TravelForm />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
